import type { Dispatch, SetStateAction } from 'react';
import Button from '@components/UI/Button';
import Modal from '@components/UI/Modal';
import FormNumberInput from '@components/UI/FormNumberInput';
import FormSelect from '@components/UI/FormSelect';
import { showToast } from '@components/Layout/Toast';
import { showSpinner, hideSpinner } from '@components/Layout/LoadingSpinner';
import type { BacktestReplenishmentRule, TradingProfile } from '@einhaus/project314-types';
import { getUnixTime } from '@einhaus/meteoric-helpers';
import type { NewBalanceReplenishmentRuleState, SaveTradingProfileSettings } from './types';

interface BalanceReplenishmentRuleModalProps {
    isOpen: boolean;
    setShowBalanceReplenishmentRulesModal: Dispatch<SetStateAction<boolean>>;
    editingBalanceReplenishmentRuleId: string | null;
    setEditingBalanceReplenishmentRuleId: Dispatch<SetStateAction<string | null>>;
    newBalanceReplenishmentRule: NewBalanceReplenishmentRuleState;
    setNewBalanceReplenishmentRule: Dispatch<SetStateAction<NewBalanceReplenishmentRuleState>>;
    backtestReplenishmentRules: BacktestReplenishmentRule[];
    setBacktestReplenishmentRules: Dispatch<SetStateAction<BacktestReplenishmentRule[]>>;
    formData: TradingProfile;
    setFormData: Dispatch<SetStateAction<TradingProfile>>;
    tradingProfileId: number;
    saveBalanceReplenishmentRule: SaveTradingProfileSettings;
}

const BalanceReplenishmentRuleModal = ({
    isOpen,
    setShowBalanceReplenishmentRulesModal,
    editingBalanceReplenishmentRuleId,
    setEditingBalanceReplenishmentRuleId,
    newBalanceReplenishmentRule,
    setNewBalanceReplenishmentRule,
    backtestReplenishmentRules,
    setBacktestReplenishmentRules,
    formData,
    setFormData,
    tradingProfileId,
    saveBalanceReplenishmentRule
}: BalanceReplenishmentRuleModalProps) => {
    // Balance Replenishment Rules Modal handlers
    const closeBalanceReplenishmentRulesModal = () => {
        setShowBalanceReplenishmentRulesModal(false);
        setEditingBalanceReplenishmentRuleId(null);

        setNewBalanceReplenishmentRule({
            balanceThresholdType: '',
            balanceThreshold: null,
            replenishmentType: '',
            replenishmentAmount: null,
            maxReplenishments: null
        });
    };

    const handleSaveBalanceReplenishmentRule = () => {
        const { balanceThresholdType, balanceThreshold, replenishmentType, replenishmentAmount, maxReplenishments } =
            newBalanceReplenishmentRule;

        if (
            !balanceThresholdType ||
            balanceThreshold === null ||
            !replenishmentType ||
            replenishmentAmount === null ||
            maxReplenishments === null
        ) {
            showToast('Please fill in all balance replenishment rule fields', 'error');
            return;
        }

        const ruleId = editingBalanceReplenishmentRuleId ?? `rule-${getUnixTime()}`;

        const newRule: BacktestReplenishmentRule = {
            id: ruleId,
            balanceThresholdType: balanceThresholdType as 'percentageDecline' | 'dollarAmountDecline',
            balanceThreshold,
            replenishmentType: replenishmentType as 'percentage' | 'dollarAmount',
            replenishmentAmount,
            maxReplenishments
        };

        const updatedRules =
            editingBalanceReplenishmentRuleId == null
                ? [...backtestReplenishmentRules, newRule]
                : backtestReplenishmentRules.map((existing) => (existing.id === ruleId ? newRule : existing));

        setBacktestReplenishmentRules(updatedRules);

        // Update the form data with the new balance replenishment rule
        const updatedFormData = {
            ...formData,
            backtestReplenishmentRules: updatedRules
        };

        setFormData(updatedFormData);
        showSpinner();

        // Use the balance replenishment rule mutation to save the updated settings
        saveBalanceReplenishmentRule(
            {
                tradingProfileId,
                settings: updatedFormData
            },
            {
                onSuccess: () => {
                    hideSpinner();
                    closeBalanceReplenishmentRulesModal();
                },
                onError: () => {
                    hideSpinner();
                }
            }
        );
    };

    return (
        <Modal
            footer={
                <>
                    <Button variant="secondary" onClick={closeBalanceReplenishmentRulesModal}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSaveBalanceReplenishmentRule}>
                        {editingBalanceReplenishmentRuleId == null ? 'Add' : 'Save'}
                    </Button>
                </>
            }
            isOpen={isOpen}
            title={editingBalanceReplenishmentRuleId == null ? 'Add Balance Replenishment Rule' : 'Edit Balance Replenishment Rule'}
            onClose={closeBalanceReplenishmentRulesModal}
        >
            <div className="mb-3">
                <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="balanceThresholdType">
                    Balance Threshold Type
                </label>
                <FormSelect
                    id="balanceThresholdType"
                    options={[
                        { value: '', label: 'Select Threshold Type' },
                        { value: 'percentageDecline', label: 'Percentage Decline' },
                        { value: 'dollarAmountDecline', label: 'Dollar Amount Decline' }
                    ]}
                    value={newBalanceReplenishmentRule.balanceThresholdType}
                    onChange={(e) => setNewBalanceReplenishmentRule((prev) => ({ ...prev, balanceThresholdType: e.target.value }))}
                />
                <small className="block text-sm text-zinc-400">How to measure the balance threshold</small>
            </div>
            <div className="mb-3">
                <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="balanceThreshold">
                    Balance Threshold
                </label>
                <FormNumberInput
                    id="balanceThreshold"
                    min={0}
                    placeholder="e.g., 20 for 20% or 5000 for $5000"
                    value={newBalanceReplenishmentRule.balanceThreshold}
                    onValueChange={(balanceThreshold) => setNewBalanceReplenishmentRule((prev) => ({ ...prev, balanceThreshold }))}
                />
                <small className="block text-sm text-zinc-400">
                    {newBalanceReplenishmentRule.balanceThresholdType === 'percentageDecline'
                        ? 'Percentage decline from initial balance that triggers replenishment'
                        : 'Dollar amount decline from initial balance that triggers replenishment'}
                </small>
            </div>
            <div className="mb-3">
                <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="replenishmentType">
                    Replenishment Type
                </label>
                <FormSelect
                    id="replenishmentType"
                    options={[
                        { value: '', label: 'Select Replenishment Type' },
                        { value: 'percentage', label: 'Percentage of Initial Balance' },
                        { value: 'dollarAmount', label: 'Dollar Amount' }
                    ]}
                    value={newBalanceReplenishmentRule.replenishmentType}
                    onChange={(e) => setNewBalanceReplenishmentRule((prev) => ({ ...prev, replenishmentType: e.target.value }))}
                />
                <small className="block text-sm text-zinc-400">How to calculate the replenishment amount</small>
            </div>
            <div className="mb-3">
                <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="replenishmentAmount">
                    Replenishment Amount
                </label>
                <FormNumberInput
                    id="replenishmentAmount"
                    min={0}
                    placeholder="e.g., 50 for 50% or 10000 for $10000"
                    value={newBalanceReplenishmentRule.replenishmentAmount}
                    onValueChange={(replenishmentAmount) => setNewBalanceReplenishmentRule((prev) => ({ ...prev, replenishmentAmount }))}
                />
                <small className="block text-sm text-zinc-400">
                    {newBalanceReplenishmentRule.replenishmentType === 'percentage'
                        ? 'Percentage of initial balance to add'
                        : 'Dollar amount to add'}
                </small>
            </div>
            <div className="mb-3">
                <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="maxReplenishments">
                    Maximum Replenishments
                </label>
                <FormNumberInput
                    integer
                    className="w-full bg-zinc-800 border border-zinc-600 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    id="maxReplenishments"
                    min={0}
                    placeholder="e.g., 3"
                    value={newBalanceReplenishmentRule.maxReplenishments}
                    onValueChange={(maxReplenishments) => setNewBalanceReplenishmentRule((prev) => ({ ...prev, maxReplenishments }))}
                />
                <small className="block text-sm text-zinc-400">Maximum number of times this rule can be applied</small>
            </div>
        </Modal>
    );
};

export default BalanceReplenishmentRuleModal;
